import huesPalette from './hues_palette.json';

export interface GuesshueState {
  targetColor: string | null;
  targetX?: number;
  targetY?: number;
  currentClue: string;
  guesses: Record<string, { x: number; y: number; color: string; playerId?: string }[]>;
  scores: Record<string, number>;
  round: number;
  phase: 'waiting' | 'giving-clue' | 'guessing' | 'stealing' | 'revealing';
  clueGiver: string | null;
  gameMode: 'cooperative' | 'teams' | 'speed';
  teams?: { '0': string[]; '1': string[] };
  teamScores?: { '0': number; '1': number };
  teamSelections?: Record<string, '0' | '1' | null>; // Track which team each player selected
  autoSelectColor?: boolean; // Whether colors are automatically selected
  speedModeWinner?: string | null; // Track who won the current speed round
  speedModeFirstGuesser?: string | null; // Track who guessed first in speed mode
  stealingTeam?: '0' | '1' | null; // Which team is currently stealing
  stealGuesses?: Record<string, { x: number; y: number; color: string }[]>; // Separate steal guesses
  roundScored?: boolean; // Track if the active team scored this round
  stealSuccessful?: boolean; // Track if the steal was successful
  nextClueGiver?: string | null; // Who will give clue next round
  nextTargetColor?: string | null; // Pre-selected color for next round
  nextTargetX?: number; // Pre-selected color X position
  nextTargetY?: number; // Pre-selected color Y position
  nextClue?: string | null; // Pre-written clue for next round
}

// Helper function to calculate grid distance
function calculateGridDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2));
}

// Helper function to generate a random color from the grid
function getRandomGridColor(): { color: string; x: number; y: number } {
  // Match Hues and Cues board dimensions
  const GRID_WIDTH = 30;
  const GRID_HEIGHT = 16;
  
  const x = Math.floor(Math.random() * GRID_WIDTH);
  // Skip row 0 (grayscale) for random selection - only select from colored rows
  const y = 1 + Math.floor(Math.random() * (GRID_HEIGHT - 1));
  
  // Get color from palette
  const color = huesPalette[y][x];
  
  return { color, x, y };
}

// Helper function to get player's team
function getPlayerTeam(playerID: string, teams?: { '0': string[]; '1': string[] }): string | null {
  if (!teams) return null;
  if (teams['0'].includes(playerID)) return '0';
  if (teams['1'].includes(playerID)) return '1';
  return null;
}

// Helper function to award points based on game mode
function awardPoints(G: GuesshueState, playerID: string, points: number) {
  G.scores[playerID] = (G.scores[playerID] || 0) + points;
  
  // In team mode, also update team scores
  if (G.gameMode === 'teams' && G.teams && G.teamScores) {
    const team = getPlayerTeam(playerID, G.teams);
    if (team !== null) {
      G.teamScores[team as '0' | '1'] = (G.teamScores[team as '0' | '1'] || 0) + points;
    }
  }
}

export const GuesshueGame = {
  name: 'guesshue',
  minPlayers: 2,
  maxPlayers: 20,
  
  setup: ({ ctx }: { ctx: any }, setupData?: any) => {
    console.log('Game setup with players:', ctx.numPlayers, 'setupData:', setupData);
    const gameMode = setupData?.settings?.gameMode;
    const isTeamMode = gameMode === 'teams' && ctx.numPlayers === 4;
    const isSpeedMode = gameMode === 'speed';
    const autoSelectColor = setupData?.settings?.autoSelectColor || false;
    
    // Initialize player scores
    const scores: Record<string, number> = {};
    for (let i = 0; i < ctx.numPlayers; i++) {
      scores[i.toString()] = 0;
    }
    
    const state: GuesshueState = {
      targetColor: null,
      currentClue: '',
      guesses: {},
      scores,
      round: 0,
      phase: 'waiting',
      clueGiver: '0', // Start with player 0 as clue giver
      gameMode: isSpeedMode ? 'speed' : (isTeamMode ? 'teams' : 'cooperative'),
      autoSelectColor,
      speedModeWinner: null,
      speedModeFirstGuesser: null,
      stealingTeam: null,
      stealGuesses: {},
      roundScored: false,
      stealSuccessful: false,
      nextClueGiver: null,
      nextTargetColor: null,
      nextTargetX: undefined,
      nextTargetY: undefined,
      nextClue: null,
    };
    
    // Set up teams for 4-player mode (but not in speed mode)
    if (isTeamMode && !isSpeedMode) {
      state.teams = {
        '0': [], // Team A - players will choose their teams
        '1': [], // Team B - players will choose their teams
      };
      state.teamScores = { '0': 0, '1': 0 };
      state.teamSelections = {};
      // Initialize team selections for all players
      for (let i = 0; i < ctx.numPlayers; i++) {
        state.teamSelections[i.toString()] = null;
      }
    }
    
    return state;
  },

  moves: {
    revealAnswer: {
      move: ({ G }: { G: GuesshueState }) => {
        // Allow clue giver to reveal answer in speed mode if no one has guessed correctly
        if (G.phase === 'guessing' && G.gameMode === 'speed' && !G.speedModeWinner) {
          G.phase = 'revealing';
          console.log('Speed mode: Revealing answer as no one guessed correctly');
        }
      },
      client: false,
    },

    giveNextClue: {
      move: ({ G, playerID }: { G: GuesshueState; playerID: string | null }, clue: string) => {
        if (!playerID || !G.nextClueGiver || playerID !== G.nextClueGiver) {
          console.log('Not the next clue giver');
          return;
        }
        
        // Validate clue (1-2 words)
        const wordCount = clue.trim().split(/\s+/).length;
        if (wordCount >= 1 && wordCount <= 2 && G.nextTargetColor) {
          G.nextClue = clue;
          console.log('Next clue prepared:', clue);
        }
      },
      client: false,
    },
    selectTeam: {
      move: ({ G, playerID }: { G: GuesshueState; playerID: string | null }, team: '0' | '1') => {
        if (!playerID || G.phase !== 'waiting' || G.gameMode !== 'teams') return;
        if (!G.teamSelections || !G.teams) return;
        
        // Remove player from their current team if they're already on one
        const currentTeam = G.teamSelections[playerID];
        if (currentTeam !== null && currentTeam !== undefined && G.teams[currentTeam]) {
          G.teams[currentTeam] = G.teams[currentTeam].filter((id: string) => id !== playerID);
        }
        
        // Add player to the new team
        G.teamSelections[playerID] = team;
        if (!G.teams[team].includes(playerID)) {
          G.teams[team].push(playerID);
        }
        
        console.log(`Player ${playerID} selected team ${team}. Teams now:`, G.teams);
      },
      client: false,
    },

    startGame: {
      move: ({ G }: { G: GuesshueState }) => {
        if (G.phase === 'waiting' && G.round === 0) {
          // In team mode, ensure teams are balanced before starting
          if (G.gameMode === 'teams' && G.teams) {
            const teamASize = G.teams['0'].length;
            const teamBSize = G.teams['1'].length;
            if (teamASize !== 2 || teamBSize !== 2) {
              console.log('Cannot start: teams are not balanced', { teamA: teamASize, teamB: teamBSize });
              return;
            }
            // Set the first clue giver from Team A
            G.clueGiver = G.teams['0'][0] || '0';
            // Set the next clue giver (first player from Team B) - alternating teams
            G.nextClueGiver = G.teams['1'][0] || '1';
          }
          G.phase = 'giving-clue';
          
          // Auto-select color if enabled
          if (G.autoSelectColor) {
            const { color, x, y } = getRandomGridColor();
            G.targetColor = color;
            G.targetX = x;
            G.targetY = y;
            console.log('Auto-selected color:', color, 'at position', x, y);
          }
        }
      },
      client: false,
    },

    selectTargetColor: {
      move: ({ G }: { G: GuesshueState }, color: string, x: number, y: number) => {
        if (G.phase === 'giving-clue') {
          G.targetColor = color;
          G.targetX = x;
          G.targetY = y;
          // Color is selected, now waiting for clue
        }
      },
      client: false,
    },

    giveClue: {
      move: ({ G, ctx }: { G: GuesshueState; ctx: any }, clue: string) => {
        // Validate clue (1-2 words)
        const wordCount = clue.trim().split(/\s+/).length;
        if (wordCount >= 1 && wordCount <= 2 && G.targetColor) {
          G.currentClue = clue;
          G.phase = 'guessing';
          G.roundScored = false; // Reset for new round
          G.stealGuesses = {}; // Clear any previous steal guesses
          
          // In team mode, determine and set the next clue giver
          if (G.gameMode === 'teams' && G.teams && G.clueGiver) {
            const currentTeam = getPlayerTeam(G.clueGiver, G.teams);
            const currentTeamPlayers = currentTeam !== null ? G.teams[currentTeam as '0' | '1'] : [];
            const currentIndex = currentTeamPlayers.indexOf(G.clueGiver);
            
            // Calculate next clue giver - always alternate between teams
            const nextTeam = currentTeam === '0' ? '1' : '0';
            const nextTeamPlayers = G.teams[nextTeam as '0' | '1'];
            
            // Find which player on the next team should go
            // If Team A Player 1 just went, next is Team B Player 1
            // If Team B Player 1 just went, next is Team A Player 2
            let nextClueGiver: string;
            if (currentIndex === 0 && nextTeamPlayers.length > 0) {
              nextClueGiver = nextTeamPlayers[0]; // First player of other team
            } else if (nextTeamPlayers.length > 1) {
              nextClueGiver = nextTeamPlayers[1]; // Second player of other team
            } else {
              nextClueGiver = nextTeamPlayers[0] || '0'; // Fallback
            }
            
            G.nextClueGiver = nextClueGiver;
            console.log('Next clue giver will be:', nextClueGiver);
            
            // Auto-select color for next clue giver if enabled
            if (G.autoSelectColor) {
              const { color, x, y } = getRandomGridColor();
              G.nextTargetColor = color;
              G.nextTargetX = x;
              G.nextTargetY = y;
              console.log('Auto-selected color for next clue giver:', color, 'at position', x, y);
            }
          }
        }
      },
      client: false,
    },

    makeGuess: {
      move: ({ G, playerID, ctx }: { G: GuesshueState; playerID: string | null; ctx: any }, x: number, y: number, color: string) => {
        console.log('SERVER: makeGuess called - playerID:', playerID, 'phase:', G.phase, 'clueGiver:', G.clueGiver);
        
        // playerID might be null if not properly set by boardgame.io
        if (playerID === null || playerID === undefined) {
          console.log('SERVER: playerID is null/undefined');
          return;
        }
        
        // Handle stealing phase
        if (G.phase === 'stealing') {
          console.log('SERVER: In stealing phase');
          
          // Check if player is on the stealing team
          if (G.gameMode === 'teams' && G.teams && G.stealingTeam !== null) {
            const playerTeam = getPlayerTeam(playerID, G.teams);
            
            if (playerTeam !== G.stealingTeam) {
              console.log('SERVER: Player is not on the stealing team');
              return;
            }
            
            // Initialize steal guesses if needed
            if (!G.stealGuesses![playerID]) {
              G.stealGuesses![playerID] = [];
            }
            
            // Each player gets only 1 steal guess
            if (G.stealGuesses![playerID].length >= 1) {
              console.log('SERVER: Already made steal guess');
              return;
            }
            
            // Add the steal guess with playerId
            G.stealGuesses![playerID].push({ x, y, color, playerId: playerID });
            console.log('SERVER: Steal guess added for player', playerID);
            
            // Check if it's correct
            if (G.targetX !== undefined && G.targetY !== undefined) {
              const distance = calculateGridDistance(x, y, G.targetX, G.targetY);
              
              if (distance === 0) {
                console.log('SERVER: Steal successful! Team scores 1 point');
                // Award 1 point to the stealing team
                if (G.teamScores) {
                  G.teamScores[G.stealingTeam] += 1;
                  // Also give the point to the individual player
                  awardPoints(G, playerID, 1);
                }
                // Mark steal as successful
                G.stealSuccessful = true;
                // Go to revealing phase
                G.phase = 'revealing';
                return;
              }
            }
            
            // Check if all stealing team members have guessed
            const stealingTeamPlayers = G.teams[G.stealingTeam];
            const allStealGuessesIn = stealingTeamPlayers.every(pid => 
              G.stealGuesses![pid] && G.stealGuesses![pid].length >= 1
            );
            
            if (allStealGuessesIn) {
              console.log('SERVER: All steal attempts used, no successful steal');
              G.phase = 'revealing';
            }
            
            return;
          }
        }
        
        if (G.phase !== 'guessing') {
          console.log('SERVER: Not in guessing phase');
          return;
        }
        
        if (playerID === G.clueGiver) {
          console.log('SERVER: Player is clue giver, cannot guess');
          return;
        }
        
        // In team mode, check if player is on the active team
        if (G.gameMode === 'teams' && G.teams && G.clueGiver !== null) {
          const playerTeam = getPlayerTeam(playerID, G.teams);
          const clueGiverTeam = getPlayerTeam(G.clueGiver, G.teams);
          
          if (playerTeam !== clueGiverTeam) {
            console.log('SERVER: Player is on opposing team, cannot guess');
            return;
          }
        }
        
        // In speed mode, check if someone already won
        if (G.gameMode === 'speed' && G.speedModeWinner !== null) {
          console.log('SERVER: Speed mode already has a winner');
          return;
        }
        
        // Initialize guesses array if needed
        if (!G.guesses[playerID]) {
          G.guesses[playerID] = [];
        }
        
        // Check guess limit (no limit in speed mode)
        if (G.gameMode !== 'speed' && G.guesses[playerID].length >= 3) {
          console.log('SERVER: Already have 3 guesses');
          return;
        }
        
        // Check if this square has already been guessed
        const alreadyGuessed = G.guesses[playerID].some(guess => guess.x === x && guess.y === y);
        if (alreadyGuessed) {
          console.log('SERVER: Square already guessed at position', x, y);
          return;
        }
        
        // Add the guess with playerId for speed mode
        G.guesses[playerID].push({ x, y, color, playerId: playerID });
        console.log('SERVER: Guess added! Total guesses for player', playerID, ':', G.guesses[playerID].length);
        console.log('SERVER: All guesses now:', JSON.stringify(G.guesses));
        
        // Check if this is a correct guess (distance 0)
        if (G.targetX !== undefined && G.targetY !== undefined) {
          const distance = calculateGridDistance(x, y, G.targetX, G.targetY);
          console.log('SERVER: Guess distance:', distance, 'from target at', G.targetX, G.targetY);
          
          if (distance === 0) {
            console.log('SERVER: Correct guess! Auto-revealing and calculating scores...');
            
            // In speed mode, first correct guesser wins immediately
            if (G.gameMode === 'speed') {
              G.speedModeWinner = playerID;
              G.speedModeFirstGuesser = playerID;
              // Award points to the winner
              awardPoints(G, playerID, 5); // 5 points for winning in speed mode
              // Award 2 points to the clue giver
              if (G.clueGiver !== null) {
                awardPoints(G, G.clueGiver, 2);
              }
              G.phase = 'revealing';
              console.log(`SERVER: Speed mode winner: ${playerID}!`);
              return;
            }
            
            // Mark that the team scored
            G.roundScored = true;
            
            // Transition to revealing phase
            G.phase = 'revealing';
            
            // Calculate scores immediately
            let totalPoints = 0;
            
            // Calculate points for each guesser
            Object.entries(G.guesses).forEach(([guesserID, guesses]) => {
              if (guesserID !== G.clueGiver) {
                // In team mode, only score players on the active team
                if (G.gameMode === 'teams' && G.teams && G.clueGiver !== null) {
                  const guesserTeam = getPlayerTeam(guesserID, G.teams);
                  const clueGiverTeam = getPlayerTeam(G.clueGiver, G.teams);
                  if (guesserTeam !== clueGiverTeam) {
                    return; // Skip players on opposing team
                  }
                }
                
                // Find the best guess (closest to target)
                let bestDistance = Infinity;
                guesses.forEach((guess: any) => {
                  const dist = calculateGridDistance(guess.x, guess.y, G.targetX!, G.targetY!);
                  if (dist < bestDistance) {
                    bestDistance = dist;
                  }
                });
                
                // Award points based on distance (more forgiving)
                let points = 0;
                if (bestDistance === 0) points = 4;      // Exact match
                else if (bestDistance === 1) points = 3; // Adjacent
                else if (bestDistance === 2) points = 2; // 2 squares away
                else if (bestDistance === 3) points = 1; // 3 squares away
                
                if (points > 0) {
                  awardPoints(G, guesserID, points);
                  totalPoints += points;
                  console.log(`SERVER: Player ${guesserID} scored ${points} points (best distance: ${bestDistance})`);
                }
              }
            });
            
            // Clue giver gets the sum of all points
            if (totalPoints > 0 && G.clueGiver !== null) {
              awardPoints(G, G.clueGiver, totalPoints);
              console.log(`SERVER: Clue giver ${G.clueGiver} scored ${totalPoints} points`);
              G.roundScored = true; // Mark that points were scored
            }
          }
        }
        
        // Check if all non-clue-givers on the active team have made 3 guesses (not in speed mode)
        if (G.gameMode !== 'speed') {
          let playersToCheck = Object.keys(G.scores).filter(pid => pid !== G.clueGiver);
          
          // In team mode, only check players on the active team
          if (G.gameMode === 'teams' && G.teams && G.clueGiver !== null) {
            const clueGiverTeam = getPlayerTeam(G.clueGiver, G.teams);
            playersToCheck = playersToCheck.filter(pid => getPlayerTeam(pid, G.teams) === clueGiverTeam);
          }
          
          const allGuessersFinished = playersToCheck.every(pid => G.guesses[pid] && G.guesses[pid].length >= 3);
            
            if (allGuessersFinished && G.phase === 'guessing') {
              console.log('SERVER: All players have used their guesses. Auto-revealing...');
              
              // Transition to revealing phase
              G.phase = 'revealing';
              
              // Calculate scores only if we haven't already (from correct guess)
              if (G.targetX !== undefined && G.targetY !== undefined) {
                let totalPoints = 0;
                
                Object.entries(G.guesses).forEach(([guesserID, guesses]) => {
                  if (guesserID !== G.clueGiver) {
                    // In team mode, only score players on the active team
                    if (G.gameMode === 'teams' && G.teams && G.clueGiver !== null) {
                      const guesserTeam = getPlayerTeam(guesserID, G.teams);
                      const clueGiverTeam = getPlayerTeam(G.clueGiver, G.teams);
                      if (guesserTeam !== clueGiverTeam) {
                        return; // Skip players on opposing team
                      }
                    }
                    
                    let bestDistance = Infinity;
                    guesses.forEach((guess: any) => {
                      const dist = calculateGridDistance(guess.x, guess.y, G.targetX!, G.targetY!);
                      if (dist < bestDistance) {
                        bestDistance = dist;
                      }
                    });
                    
                    let points = 0;
                    if (bestDistance === 0) points = 4;
                    else if (bestDistance === 1) points = 3;
                    else if (bestDistance === 2) points = 2;
                    else if (bestDistance === 3) points = 1;
                    
                    if (points > 0) {
                      awardPoints(G, guesserID, points);
                      totalPoints += points;
                      console.log(`SERVER: Player ${guesserID} scored ${points} points (best distance: ${bestDistance})`);
                    }
                  }
                });
                
                if (totalPoints > 0 && G.clueGiver !== null) {
                  awardPoints(G, G.clueGiver, totalPoints);
                  console.log(`SERVER: Clue giver ${G.clueGiver} scored ${totalPoints} points`);
                  G.roundScored = true; // Mark that points were scored
                }
                
                // Check for steal opportunity in team mode
                if (G.gameMode === 'teams' && !G.roundScored && G.teams && G.clueGiver !== null) {
                  const clueGiverTeam = getPlayerTeam(G.clueGiver, G.teams);
                  const opposingTeam = clueGiverTeam === '0' ? '1' : '0';
                  
                  console.log('SERVER: No points scored, initiating steal phase for team', opposingTeam);
                  G.phase = 'stealing';
                  G.stealingTeam = opposingTeam;
                  G.stealGuesses = {};
                  return; // Don't go to revealing yet
                }
              }
            }
          }
      },
      client: false,
    },


    nextRound: {
      move: ({ G, ctx }: { G: GuesshueState; ctx: any }) => {
        if (G.phase === 'revealing' || (G.phase === 'waiting' && G.round > 0)) {
          G.round += 1;
          
          // Check if we have a pre-prepared clue to use
          const hasPreparedClue = G.nextTargetColor && G.nextClue && G.nextClueGiver;
          
          if (hasPreparedClue && G.gameMode === 'teams') {
            // Use the pre-prepared clue and color
            G.targetColor = G.nextTargetColor;
            G.targetX = G.nextTargetX;
            G.targetY = G.nextTargetY;
            G.currentClue = G.nextClue!;
            G.clueGiver = G.nextClueGiver!;
            G.phase = 'guessing'; // Skip directly to guessing since clue is ready
            
            // Calculate next clue giver for the following round
            if (G.teams) {
              const currentTeam = getPlayerTeam(G.clueGiver, G.teams);
              const nextTeam = currentTeam === '0' ? '1' : '0';
              const nextTeamPlayers = G.teams[nextTeam as '0' | '1'];
              
              // Find which player on the next team should go
              const currentTeamPlayers = G.teams[currentTeam as '0' | '1'];
              const currentIndex = currentTeamPlayers.indexOf(G.clueGiver);
              
              let nextClueGiver: string;
              if (currentIndex === 0 && nextTeamPlayers.length > 0) {
                nextClueGiver = nextTeamPlayers[0]; // First player of other team
              } else if (nextTeamPlayers.length > 1) {
                nextClueGiver = nextTeamPlayers[1]; // Second player of other team
              } else {
                nextClueGiver = nextTeamPlayers[0] || '0'; // Fallback
              }
              
              G.nextClueGiver = nextClueGiver;
              
              // Auto-select color for the new next clue giver
              if (G.autoSelectColor) {
                const { color, x, y } = getRandomGridColor();
                G.nextTargetColor = color;
                G.nextTargetX = x;
                G.nextTargetY = y;
                console.log('Auto-selected color for next clue giver:', color);
              }
            }
            
            console.log('Using pre-prepared clue:', G.currentClue);
          } else {
            // Normal flow without pre-prepared clue
            G.targetColor = null;
            G.targetX = undefined;
            G.targetY = undefined;
            G.currentClue = '';
            G.phase = 'giving-clue';
          }
          
          // Reset all other fields
          G.guesses = {};
          G.speedModeWinner = null;
          G.speedModeFirstGuesser = null;
          G.stealingTeam = null;
          G.stealGuesses = {};
          G.roundScored = false;
          G.stealSuccessful = false;
          
          // Clear next round preparation
          G.nextTargetColor = null;
          G.nextTargetX = undefined;
          G.nextTargetY = undefined;
          G.nextClue = null;
          
          // Auto-select color if enabled
          if (G.autoSelectColor) {
            const { color, x, y } = getRandomGridColor();
            G.targetColor = color;
            G.targetX = x;
            G.targetY = y;
            console.log('Auto-selected color for round', G.round + 1, ':', color, 'at position', x, y);
          }
          
          // Update clue giver only if we didn't use a pre-prepared clue
          if (!hasPreparedClue) {
            // In speed mode, rotate clue giver
            if (G.gameMode === 'speed') {
              G.clueGiver = String((parseInt(G.clueGiver || '0') + 1) % ctx.numPlayers);
            }
            // In team mode, ensure alternating between teams
            else if (G.gameMode === 'teams' && G.teams) {
              // Use nextClueGiver if available
              if (G.nextClueGiver) {
                G.clueGiver = G.nextClueGiver;
              } else {
                // Fallback to calculating it
                const currentTeam = getPlayerTeam(G.clueGiver || '0', G.teams);
                const currentTeamPlayers = currentTeam !== null ? G.teams[currentTeam as '0' | '1'] : [];
                const currentIndex = currentTeamPlayers.indexOf(G.clueGiver || '0');
                
                if (currentIndex >= currentTeamPlayers.length - 1) {
                  const nextTeam = currentTeam === '0' ? '1' : '0';
                  G.clueGiver = G.teams[nextTeam][0] || '0';
                } else {
                  G.clueGiver = currentTeamPlayers[currentIndex + 1];
                }
              }
            } else {
              // In cooperative mode, just alternate between players
              G.clueGiver = String((parseInt(G.clueGiver || '0') + 1) % ctx.numPlayers);
            }
          }
        }
      },
      client: false,
    },
    
    playAgain: {
      move: ({ G, ctx }: { G: GuesshueState; ctx: any }) => {
        // Reset all game state but keep players and teams
        G.targetColor = null;
        G.targetX = undefined;
        G.targetY = undefined;
        G.currentClue = '';
        G.guesses = {};
        G.round = 0;
        G.speedModeWinner = null;
        G.speedModeFirstGuesser = null;
        G.stealingTeam = null;
        G.stealGuesses = {};
        G.roundScored = false;
        G.stealSuccessful = false;
        G.nextClueGiver = null;
        G.nextTargetColor = null;
        G.nextTargetX = undefined;
        G.nextTargetY = undefined;
        G.nextClue = null;
        
        // Reset scores
        Object.keys(G.scores).forEach(key => {
          G.scores[key] = 0;
        });
        
        // Reset team scores if in team mode
        if (G.teamScores) {
          G.teamScores['0'] = 0;
          G.teamScores['1'] = 0;
        }
        
        // Start new game immediately
        G.phase = 'giving-clue';
        
        // Set up clue giver based on mode
        if (G.gameMode === 'teams' && G.teams) {
          // Start with first player of Team A
          G.clueGiver = G.teams['0'][0] || '0';
          // Set next clue giver to first player of Team B
          G.nextClueGiver = G.teams['1'][0] || '1';
          
          // Auto-select color for next clue giver if enabled
          if (G.autoSelectColor && G.nextClueGiver) {
            const { color, x, y } = getRandomGridColor();
            G.nextTargetColor = color;
            G.nextTargetX = x;
            G.nextTargetY = y;
          }
        } else {
          // For other modes, start with player 0
          G.clueGiver = '0';
        }
        
        // Auto-select color if enabled
        if (G.autoSelectColor) {
          const { color, x, y } = getRandomGridColor();
          G.targetColor = color;
          G.targetX = x;
          G.targetY = y;
        }
      },
      client: false,
    },
  },

  turn: {
    activePlayers: { all: 'play' },
  },

  endIf: ({ G }: { G: GuesshueState }) => {
    // Game ends after 8 rounds are completed (when trying to start round 9)
    if (G.round >= 8 && G.phase === 'revealing') {
      if (G.gameMode === 'teams' && G.teamScores) {
        // In team mode, compare team scores
        const winner = G.teamScores['0'] > G.teamScores['1'] ? 'Team A' : 
                      G.teamScores['1'] > G.teamScores['0'] ? 'Team B' : 
                      'Tie';
        return { winner };
      } else {
        // In cooperative mode, find the player with highest score
        const winner = Object.entries(G.scores).reduce((a: [string, number], b: [string, number]) => 
          a[1] > b[1] ? a : b
        )[0];
        return { winner };
      }
    }
  },
};